import { useCallback, useEffect, useRef, useState } from "react";
import type { HomeSurface } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type HomeSurfaceState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; surface: HomeSurface };

export interface UseHomeSurfaceResult {
  state: HomeSurfaceState;
  // Re-runs the fetch without resetting to "loading". Shape copied from
  // useLabResults.ts.
  refetch: () => void;
}

// Fetch on mount plus on demand via refetch, no poll: the unread pin is
// already covered by the existing 45s useChatSummary poll driving the tab
// badge, so a second poll here would be redundant work.
export function useHomeSurface(): UseHomeSurfaceResult {
  const api = useApi();
  const [state, setState] = useState<HomeSurfaceState>({ status: "loading" });
  const cancelledRef = useRef(false);

  const fetchSurface = useCallback(() => {
    api.home
      .get()
      .then((surface) => {
        if (!cancelledRef.current) {
          setState({ status: "success", surface });
        }
      })
      .catch((err: unknown) => {
        if (!cancelledRef.current) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load home surface" });
        }
      });
  }, [api]);

  useEffect(() => {
    cancelledRef.current = false;
    fetchSurface();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchSurface]);

  return { state, refetch: fetchSurface };
}
